'use client';

import { parseAsBoolean, parseAsStringEnum, useQueryState } from 'nuqs';

import { ReviewType } from '@/types/github';

/**
 * Review dropdown state store
 * Uses URL query parameters via nuqs for managing review dropdown state
 */

// Define the valid review type values as an array
const reviewTypeValues = [ReviewType.COMMENT, ReviewType.APPROVE, ReviewType.REQUEST_CHANGES];

/** The dropdown's URL state; a host that hides the dropdown clears these keys. */
export const reviewDropdownQueryParsers = {
  reviewOpen: parseAsBoolean.withDefault(false),
  reviewType: parseAsStringEnum(reviewTypeValues).withDefault(ReviewType.COMMENT),
};

/**
 * Hook to manage the review dropdown state
 * Returns functions to open the dropdown with specific review types
 */
export function useReviewDropdownStore() {
  // Control if the dropdown is open
  const [isOpen, setIsOpen] = useQueryState('reviewOpen', reviewDropdownQueryParsers.reviewOpen);

  // Control the selected review type
  const [reviewType, setReviewType] = useQueryState(
    'reviewType',
    reviewDropdownQueryParsers.reviewType,
  );

  // Utility functions to open the dropdown with specific review types
  const openWithComment = () => {
    setReviewType(ReviewType.COMMENT);
    setIsOpen(true);
  };

  const openWithApproval = () => {
    setReviewType(ReviewType.APPROVE);
    setIsOpen(true);
  };

  const openWithRequestChanges = () => {
    setReviewType(ReviewType.REQUEST_CHANGES);
    setIsOpen(true);
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  return {
    isOpen,
    reviewType,
    openWithComment,
    openWithApproval,
    openWithRequestChanges,
    closeDropdown,
  };
}
